import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  Input,
  Renderer2,
  signal,
  viewChild,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  PoButtonModule,
  PoModalComponent,
  PoModalModule,
  PoNotificationService,
  PoTagModule,
  PoToasterOrientation,
  PoTooltipModule,
  PoWidgetModule,
} from '@po-ui/ng-components';
import { assert, AtsUiLibModule } from 'ats-ui-lib';
import { NoStandaloneSharedModule } from '../../../../no-standalone-shared/no-standalone-shared.module';
import { JobOpportunityListItem, JobApplicationTypeEnum, UrlImagesService } from '@services/index';
import { environment } from '@environments/environment';
import { EasyApplyFormsComponent } from '@pages/easy-apply-forms/easy-apply-forms.component';
import { EasyApplySuccessComponent } from '@pages/easy-apply-success/easy-apply-success.component';
import { HowFindJobOpportunityEnum } from '@models/job-opportunity.models';
import { APPLICATION_SOURCE_QUERY_PARAM } from '../../../../root/application-source/application-source.util';

@Component({
  selector: 'ats-job-opportunity-card',
  imports: [
    CommonModule,
    PoButtonModule,
    PoTooltipModule,
    PoWidgetModule,
    PoTagModule,
    TranslateModule,
    RouterModule,
    AtsUiLibModule,
    PoModalModule,
    NoStandaloneSharedModule,
    EasyApplyFormsComponent,
    EasyApplySuccessComponent,
  ],
  templateUrl: './job-opportunity-card.component.html',
  styleUrl: './job-opportunity-card.component.css',
})
export class JobOpportunityCardComponent {
  private readonly itemSignal = signal<JobOpportunityListItem | undefined>(undefined);

  @Input()
  public set item(value: JobOpportunityListItem | undefined) {
    this.itemSignal.set(value);
  }

  protected readonly currentItem = this.itemSignal.asReadonly();

  protected readonly showWhatsAppApplication = computed(() => {
    const item = this.currentItem();

    return (
      item?.jobApplicationType === JobApplicationTypeEnum.Simplified &&
      item.isWhatsAppJobApplicationEnabled &&
      !!item.whatsAppLink
    );
  });

  // The Novo Trampo portal is itself the origin: when no other origin is
  // defined, applications started here default to Novo Trampo.
  protected readonly defaultApplicationSource = HowFindJobOpportunityEnum.novoTrampo;

  protected disableSendApplication = signal<boolean>(true);

  protected poModal = viewChild(PoModalComponent);

  protected easyApplyFormsComponent = viewChild(EasyApplyFormsComponent);

  protected jobApplicationTypeEnum = JobApplicationTypeEnum;

  protected easyApplySuccessful = signal<boolean>(false);

  protected initModalEasyApply = signal<boolean>(false);

  protected sendingApplication = signal<boolean>(false);

  protected enableModal = signal<boolean>(false);

  protected easyApplyFormLoading = computed(
    () => this.easyApplyFormsComponent()?.loadingForm() ?? true,
  );

  protected getIconUrl = computed(() => {
    const item = this.currentItem();

    return this.urlImagesService.getCompanyLogo(item?.subdomain, item?.companyIconId);
  });

  protected careerPageUrl = computed(() => {
    const item = this.currentItem();

    if (item) return new URL(`${item.subdomain}`, environment.careerPageUrl);

    return new URL(environment.careerPageUrl);
  });

  constructor(
    private readonly router: Router,
    private readonly renderer: Renderer2,
    private readonly notificationService: PoNotificationService,
    private readonly translateService: TranslateService,
    private readonly cd: ChangeDetectorRef,
    private readonly urlImagesService: UrlImagesService,
  ) {}

  public navigateToApplicancy(): string {
    if (!this.currentItem()) return '';

    return this.createUrlJobOpportunityApplication();
  }

  protected createUrlJobOpportunityApplication(): string {
    const item = this.currentItem()!;

    return this.router.serializeUrl(
      this.router.createUrlTree(['main', 'applicant', 'job-opportunity-application', item.id], {
        queryParams: {
          tenantId: item.tenantId,
          source: 'portal',
          [APPLICATION_SOURCE_QUERY_PARAM]: this.defaultApplicationSource,
        },
      }),
    );
  }

  public navigateToCareerPage(): string {
    const item = this.currentItem();

    if (!item) return '';

    const url = new URL(item.pageUrl!);

    url.searchParams.set('source', 'portal');
    url.searchParams.set(APPLICATION_SOURCE_QUERY_PARAM, String(this.defaultApplicationSource));

    return url.toString();
  }

  private recreateModal(): void {
    this.enableModal.set(true);
    this.cd.detectChanges();
  }

  public openEasyApply(event: Event): void {
    this.recreateModal();

    this.initModalEasyApply.set(true);
    this.poModal()?.open();
    event.preventDefault();
    event.stopPropagation();
    this.renderer.setStyle(document.body, 'overflow', 'hidden');
  }

  public closeEasyApply(): void {
    this.enableModal.set(false);
    this.renderer.removeStyle(document.body, 'overflow');
    if (this.easyApplySuccessful()) {
      this.easyApplySuccessful.set(false);
      this.initModalEasyApply.set(false);
    }
  }

  public easyApplyFormStatusChanges(status: string): void {
    this.sendingApplication.set(status === 'PENDING');
    this.disableSendApplication.set(status !== 'VALID');
  }

  public async onSubmitEasyApply(): Promise<void> {
    try {
      assert(this.easyApplyFormsComponent() !== null);
      this.sendingApplication.set(true);
      await this.easyApplyFormsComponent()!.onSubmit();
      this.easyApplySuccessful.set(true);
    } catch {
      this.notifyErrorTryAgain();
    } finally {
      this.sendingApplication.set(false);
    }
  }

  protected notifyErrorTryAgain(): void {
    const translations = this.translateService.instant(['l-error-try-again']);

    this.notificationService.error({
      message: translations['l-error-try-again'],
      orientation: PoToasterOrientation.Top,
    });
  }
}
